import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { createJob } from '../services/jobService';
import { getCurrentUser } from '../services/authService';
import SkillsPicker from '../components/SkillsPicker';
import { validateJobForm, fieldErrorsFromServer, todayInJobTimeZone, maxStartDate, EMPLOYMENT_TYPES, DURATION_UNITS } from '../utils/jobValidation';

// ─── Constants ──────────────────────────────────────────────────────────────

const STATES = [
  { code: 'AL', name: 'Alabama' },      { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },      { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },   { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },      { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },       { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },     { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },         { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },     { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },        { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },{ code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },    { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },     { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },     { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },{ code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },   { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },{ code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },         { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },       { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },        { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },      { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },   { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },    { code: 'WY', name: 'Wyoming' },
];


// ─── Styles ──────────────────────────────────────────────────────────────────

const inputStyle = (hasError = false) => ({
  width: '100%',
  padding: '0.5rem',
  border: `1px solid ${hasError ? '#dc2626' : '#d1d5db'}`,
  borderRadius: '4px',
  fontSize: '16px',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
});

const errorStyle = { color: '#dc2626', fontSize: '13px', marginTop: '4px' };
const labelStyle = { display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '14px' };
const subLabelStyle = { display: 'block', marginBottom: '0.5rem', fontWeight: '400', fontSize: '13px', color: '#6b7280' };
const helpStyle = { fontSize: '12px', color: '#6b7280', marginTop: '4px' };
const fieldGroup = { marginBottom: '1rem' };

// ─── Toggle ──────────────────────────────────────────────────────────────────

function Toggle({ checked, onChange }) {
  return (
    <div
      onClick={() => onChange(!checked)}
      style={{
        width: '44px',
        height: '24px',
        borderRadius: '12px',
        backgroundColor: checked ? '#0f766e' : '#d1d5db',
        position: 'relative',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
        flexShrink: 0,
      }}
    >
      <div style={{
        position: 'absolute',
        top: '2px',
        left: checked ? '22px' : '2px',
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: '#fff',
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      }} />
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

function PostJobPage() {
  const navigate = useNavigate();
  const user = getCurrentUser();

  const [formData, setFormData] = useState({
    title: '',
    companyName: '',
    description: '',
    type: 'Full-time',
    positions: 1,
    salaryMin: '',
    salaryMax: '',
    jobDurationValue: '',
    jobDurationUnit: 'Weeks',
    startDate: '',
    endDate: '',
    state: '',
    city: '',
    country: 'US',
    expiryDays: 60,
    requirements: '',
    benefits: '',
    skills: [],
    i9Required: true,
    secondLanguage: '',
  });

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(false);
  const [allCities, setAllCities] = useState([]);
  const [showI9Modal, setShowI9Modal] = useState(false);

  useEffect(() => {
    if (!user) return;
    const loadData = async () => {
      try {
        const [userSnap, citiesSnap] = await Promise.all([
          getDoc(doc(db, 'users', user.uid)),
          getDocs(collection(db, 'cities')),
        ]);

        const companyName = userSnap.data()?.profile?.company || '';

        const cityList = citiesSnap.docs
          .map(d => ({ cityId: d.id, ...d.data() }))
          .filter(c => c.enabled);

        setFormData(prev => ({ ...prev, companyName }));
        setAllCities(cityList);
      } catch (err) {
        console.error('PostJobPage: loadData error', err);
      }
    };
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'state') updated.city = '';
      return updated;
    });
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const filteredCities = allCities.filter(c => c.state === formData.state);
  const noEnabledCities = formData.state !== '' && filteredCities.length === 0;

  function buildJobData(status) {
    return {
      title: formData.title.trim(),
      companyName: formData.companyName,
      description: formData.description.trim(),
      type: formData.type,
      positions: Number(formData.positions),
      salaryMin: formData.salaryMin !== '' ? Number(formData.salaryMin) : null,
      salaryMax: formData.salaryMax !== '' ? Number(formData.salaryMax) : null,
      jobDurationValue: formData.jobDurationValue !== '' ? Number(formData.jobDurationValue) : null,
      jobDurationUnit: formData.jobDurationValue !== '' ? formData.jobDurationUnit : null,
      startDate: formData.startDate || null,
      endDate: formData.type !== 'Full-time' ? (formData.endDate || null) : null,
      city: formData.city || null,
      state: formData.state || null,
      country: formData.country,
      expiryDays: Number(formData.expiryDays),
      requirements: formData.requirements.split('\n').filter(r => r.trim()),
      benefits: formData.benefits.split('\n').filter(b => b.trim()),
      skills: formData.skills,
      i9Required: formData.i9Required,
      secondLanguage: formData.secondLanguage || null,
      status,
    };
  }

  function validate(isDraft) {
    return validateJobForm(formData, { isDraft, mode: 'create', noEnabledCities });
  }

  // Server invalid-argument errors carry { field, code }; show them per field.
  function showServerError(err, fallback) {
    const fieldErrs = fieldErrorsFromServer(err);
    if (Object.keys(fieldErrs).length > 0) {
      setErrors(fieldErrs);
      setSubmitError('Please fix the highlighted fields.');
    } else {
      setSubmitError(fallback);
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    const errs = validate(false);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setLoading(true);
    try {
      await createJob(buildJobData('active'), user.uid);
      navigate('/manage-jobs');
    } catch (err) {
      showServerError(err, 'Failed to create job. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    setSubmitError('');
    const errs = validate(true);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setLoading(true);
    try {
      await createJob(buildJobData('draft'), user.uid);
      navigate('/manage-jobs');
    } catch (err) {
      showServerError(err, 'Failed to save draft. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isFullTime = formData.type === 'Full-time';
  const today = todayInJobTimeZone();
  const startDateMax = maxStartDate(today);
  const endDateMin = formData.startDate || today;

  return (
    <>
    <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ backgroundColor: '#F8FAFC', padding: '2rem', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>

        {submitError && (
          <div style={{ backgroundColor: '#fee2e2', color: '#991b1b', padding: '0.75rem', borderRadius: '4px', marginBottom: '1rem' }}>
            {submitError}
          </div>
        )}

        {/* noValidate (QA 2.5.18, Prompt #496): validateJobForm covers every native
            check, so our messages show instead of the browser's; the inputs keep
            min/max so the date picker still greys out out-of-range days. */}
        <form noValidate onSubmit={handleSubmit}>

          {/* 1 — Job Title */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Job Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., Construction Worker"
              style={inputStyle(!!errors.title)}
            />
            {errors.title && <div style={errorStyle}>{errors.title}</div>}
          </div>

          {/* 2 — Company Name (read-only) */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Company Name</label>
            <input
              type="text"
              value={formData.companyName}
              readOnly
              placeholder="Loading…"
              style={{ ...inputStyle(false), backgroundColor: '#f3f4f6', cursor: 'not-allowed', color: '#6b7280' }}
            />
            <div style={helpStyle}>Auto-filled from your company profile</div>
          </div>

          {/* 3 — Job Description */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Job Description *</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              placeholder="Describe the role, responsibilities, and what you're looking for…"
              style={{ ...inputStyle(!!errors.description), resize: 'vertical' }}
            />
            {errors.description && <div style={errorStyle}>{errors.description}</div>}
          </div>

          {/* 4 — Second Language */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Second Language (optional)</label>
            <select
              name="secondLanguage"
              value={formData.secondLanguage}
              onChange={handleChange}
              style={inputStyle(false)}
            >
              <option value="">None</option>
              <option value="es">Español (Spanish)</option>
              <option value="zh">中文简体 (Chinese Simplified)</option>
              <option value="zh-TW">中文繁體 (Chinese Traditional)</option>
              <option value="ko">한국어 (Korean)</option>
              <option value="ja">日本語 (Japanese)</option>
            </select>
            <div style={helpStyle}>Translate this job posting into a second language for workers</div>
          </div>

          {/* 5 — Employment Type */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Employment Type *</label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              style={inputStyle(!!errors.type)}
            >
              {EMPLOYMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            {errors.type && <div style={errorStyle}>{errors.type}</div>}
          </div>

          {/* 5 — Number of Positions */}
          <div style={fieldGroup}>
            <label style={labelStyle}>How many workers do you need?</label>
            <input
              type="number"
              name="positions"
              value={formData.positions}
              onChange={handleChange}
              min="1"
              max="999"
              step="1"
              style={{ ...inputStyle(!!errors.positions), width: '120px' }}
            />
            {errors.positions && <div style={errorStyle}>{errors.positions}</div>}
          </div>

          {/* 6 — Pay Range */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Pay Range <span style={{ fontWeight: '400', color: '#6b7280' }}>(optional)</span></label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={subLabelStyle}>Min Pay</label>
                <input
                  type="number"
                  name="salaryMin"
                  value={formData.salaryMin}
                  onChange={handleChange}
                  placeholder="e.g. 15"
                  min="0.01"
                  step="0.01"
                  style={inputStyle(!!errors.salaryMin)}
                />
                {errors.salaryMin && <div style={errorStyle}>{errors.salaryMin}</div>}
              </div>
              <div>
                <label style={subLabelStyle}>Max Pay</label>
                <input
                  type="number"
                  name="salaryMax"
                  value={formData.salaryMax}
                  onChange={handleChange}
                  placeholder="e.g. 25"
                  min="0.01"
                  step="0.01"
                  style={inputStyle(!!errors.salaryMax)}
                />
                {errors.salaryMax && <div style={errorStyle}>{errors.salaryMax}</div>}
              </div>
            </div>
            <div style={helpStyle}>Pay period and details are agreed between employer and worker</div>
          </div>

          {/* 7 — Job Duration */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Estimated Duration <span style={{ fontWeight: '400', color: '#6b7280' }}>(optional)</span></label>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '1rem' }}>
              <input
                type="number"
                name="jobDurationValue"
                value={formData.jobDurationValue}
                onChange={handleChange}
                placeholder="e.g. 3"
                min="1"
                step="1"
                style={inputStyle(!!errors.jobDurationValue)}
              />
              <select
                name="jobDurationUnit"
                value={formData.jobDurationUnit}
                onChange={handleChange}
                style={inputStyle(false)}
              >
                {DURATION_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            {errors.jobDurationValue && <div style={errorStyle}>{errors.jobDurationValue}</div>}
          </div>

          {/* 8 — Start Date */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Start Date *</label>
            <input
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
              min={today}
              max={startDateMax}
              style={inputStyle(!!errors.startDate)}
            />
            {errors.startDate && <div style={errorStyle}>{errors.startDate}</div>}
          </div>

          {/* 9 — End Date (hidden for Full-time) */}
          <div style={fieldGroup}>
            <label style={labelStyle}>End Date {!isFullTime && '*'}</label>
            {isFullTime ? (
              <div style={{ fontSize: '14px', color: '#6b7280', padding: '0.4rem 0', fontStyle: 'italic' }}>
                Ongoing (Full-time position)
              </div>
            ) : (
              <>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  min={endDateMin}
                  style={inputStyle(!!errors.endDate)}
                />
                {errors.endDate && <div style={errorStyle}>{errors.endDate}</div>}
              </>
            )}
          </div>

          {/* 10 — Location */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Location *</label>

            {/* State */}
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={subLabelStyle}>State</label>
              <select
                name="state"
                value={formData.state}
                onChange={handleChange}
                style={inputStyle(!!errors.state)}
              >
                <option value="">Select state…</option>
                {STATES.map(s => (
                  <option key={s.code} value={s.code}>{s.name} ({s.code})</option>
                ))}
              </select>
              {errors.state && <div style={errorStyle}>{errors.state}</div>}
            </div>

            {/* City */}
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={subLabelStyle}>City</label>
              {noEnabledCities ? (
                <div style={{
                  fontSize: '13px',
                  color: '#6b7280',
                  padding: '0.6rem 0.75rem',
                  backgroundColor: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                }}>
                  No cities currently available in this state.{' '}
                  Contact <a href="mailto:support@workrmatch.com" style={{ color: '#0f766e' }}>support@workrmatch.com</a>{' '}
                  to request your city.
                </div>
              ) : (
                <select
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  disabled={!formData.state}
                  style={{
                    ...inputStyle(!!errors.city),
                    opacity: !formData.state ? 0.6 : 1,
                    cursor: !formData.state ? 'not-allowed' : 'default',
                    backgroundColor: !formData.state ? '#f3f4f6' : 'white',
                  }}
                >
                  <option value="">
                    {formData.state ? 'Select city…' : 'Select a state first'}
                  </option>
                  {filteredCities.map(c => (
                    <option key={c.cityId} value={c.name}>{c.name}</option>
                  ))}
                </select>
              )}
              {errors.city && <div style={errorStyle}>{errors.city}</div>}
            </div>

            {/* Country */}
            <div>
              <label style={subLabelStyle}>Country</label>
              <input
                type="text"
                value={formData.country === 'US' ? 'United States' : formData.country}
                readOnly
                style={{ ...inputStyle(false), backgroundColor: '#f3f4f6', cursor: 'not-allowed', color: '#6b7280' }}
              />
            </div>
          </div>

          {/* 12 — Job Expires After */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Job Expires After</label>
            <select
              name="expiryDays"
              value={formData.expiryDays}
              onChange={handleChange}
              style={inputStyle(false)}
            >
              <option value={30}>30 days</option>
              <option value={60}>60 days (recommended)</option>
              <option value={90}>90 days</option>
            </select>
          </div>

          {/* 13 — Requirements */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Requirements * <span style={{ fontWeight: '400', color: '#6b7280' }}>(one per line)</span></label>
            <textarea
              name="requirements"
              value={formData.requirements}
              onChange={handleChange}
              rows="4"
              placeholder={'Experience in construction\nValid driver\'s license\nPhysical fitness'}
              style={{ ...inputStyle(!!errors.requirements), resize: 'vertical' }}
            />
            {errors.requirements && <div style={errorStyle}>{errors.requirements}</div>}
          </div>

          {/* 13b — Skills */}
          <div style={fieldGroup}>
            <SkillsPicker
              value={formData.skills}
              onChange={(skills) => setFormData(prev => ({ ...prev, skills }))}
            />
          </div>

          {/* 14 — Benefits */}
          <div style={{ ...fieldGroup, marginBottom: '1.25rem' }}>
            <label style={labelStyle}>Benefits <span style={{ fontWeight: '400', color: '#6b7280' }}>(one per line)</span></label>
            <textarea
              name="benefits"
              value={formData.benefits}
              onChange={handleChange}
              rows="4"
              placeholder={'Health insurance\nPaid time off\nTraining opportunities'}
              style={{ ...inputStyle(!!errors.benefits), resize: 'vertical' }}
            />
            {errors.benefits && <div style={errorStyle}>{errors.benefits}</div>}
          </div>

          {/* 15 — I-9 Verification */}
          <div style={{
            ...fieldGroup,
            padding: '1rem',
            backgroundColor: formData.i9Required ? '#f0fdfa' : '#fffbeb',
            border: `1px solid ${formData.i9Required ? '#99f6e4' : '#f59e0b'}`,
            borderRadius: '6px',
            marginBottom: '1.5rem',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Toggle
                checked={formData.i9Required}
                onChange={val => {
                  if (!val) {
                    setShowI9Modal(true);
                  } else {
                    setFormData(prev => ({ ...prev, i9Required: true }));
                  }
                }}
              />
              <label style={{ ...labelStyle, margin: 0, cursor: 'pointer', fontSize: '14px' }}>
                Require I-9 Verification
              </label>
            </div>
            <div style={{ ...helpStyle, marginTop: '8px', color: formData.i9Required ? '#0f766e' : '#92400e' }}>
              {formData.i9Required
                ? 'Workers will complete I-9 verification through WorkBright after offer acceptance'
                : 'I-9 verification disabled. You have accepted full legal responsibility for employment eligibility compliance.'}
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              type="button"
              disabled={loading}
              onClick={handleSaveDraft}
              style={{
                flex: 1,
                backgroundColor: 'white',
                color: '#374151',
                padding: '0.75rem',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                fontSize: '15px',
                fontWeight: '500',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.5 : 1,
              }}
            >
              Save as Draft
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                flex: 2,
                backgroundColor: '#E8590C',
                color: 'white',
                padding: '0.75rem',
                borderRadius: '6px',
                border: 'none',
                fontSize: '15px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.5 : 1,
              }}
            >
              {loading ? 'Posting...' : 'Post Job'}
            </button>
          </div>

        </form>
      </div>
    </div>

    {/* I-9 Legal Disclaimer Modal */}
    {showI9Modal && (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}>
        <div style={{
          backgroundColor: 'white',
          border: '2px solid #f59e0b',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '480px',
          width: '90%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#92400e', marginBottom: '16px' }}>
            Disable I-9 Verification?
          </div>
          <ul style={{ margin: '0 0 20px 0', padding: '0 0 0 20px' }}>
            <li style={{ fontSize: '13px', color: '#374151', lineHeight: '1.6', marginBottom: '8px' }}>
              You are solely responsible for verifying the employment eligibility of all workers hired through this posting
            </li>
            <li style={{ fontSize: '13px', color: '#374151', lineHeight: '1.6', marginBottom: '8px' }}>
              WorkrMatch accepts no liability for any violations of employment eligibility verification laws, including but not limited to the Immigration Reform and Control Act (IRCA)
            </li>
            <li style={{ fontSize: '13px', color: '#374151', lineHeight: '1.6' }}>
              You may be subject to civil and criminal penalties for hiring workers without proper I-9 documentation
            </li>
          </ul>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <button
              onClick={() => { setFormData(prev => ({ ...prev, i9Required: false })); setShowI9Modal(false); }}
              style={{
                backgroundColor: '#f59e0b', color: 'white', border: 'none',
                padding: '10px 16px', borderRadius: '6px',
                fontSize: '14px', fontWeight: '600', cursor: 'pointer',
              }}
            >
              I Accept Full Responsibility
            </button>
            <button
              onClick={() => { setFormData(prev => ({ ...prev, i9Required: true })); setShowI9Modal(false); }}
              style={{
                backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db',
                padding: '10px 16px', borderRadius: '6px',
                fontSize: '14px', fontWeight: '500', cursor: 'pointer',
              }}
            >
              Cancel — Keep I-9 Enabled
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

export default PostJobPage;
